import type { Job } from '@/api/types'

/**
 * The states a job does not leave. Everything else is still going.
 *
 * Matched as "not terminal" rather than as a list of running states, because
 * each job type adds its own intermediate ones -- a file import passes through
 * several -- and a new one would otherwise be read as finished.
 */
const TERMINAL_STATES = new Set(['finished', 'failed', 'cancelled'])

export function isJobRunning(job: Job): boolean {
  return !TERMINAL_STATES.has(job.state)
}

export function isJobFailed(job: Job): boolean {
  return job.state === 'failed'
}

/** Poll fast at first, then slower, then not at all. */
const FIRST_DELAY = 200
const STEPS: { after: number; delay: number }[] = [
  { after: 2_000, delay: 1_000 },
  { after: 10_000, delay: 2_000 },
  { after: 60_000, delay: 5_000 },
]
const GIVE_UP_AFTER = 10 * 60_000

/**
 * How long to wait before asking about the jobs again, or `false` to stop.
 *
 * A pure function of the jobs and the clock. Upstream carries the same policy in
 * a counter it has to remember to reset -- attempts remaining, last seen job ids,
 * a timeout id -- and the same information is already in the data: how long the
 * oldest unfinished job has been going says everything the counter was tracking.
 *
 * The shape matters more than the numbers. A duplicate of a small database is
 * done in well under a second, so a fixed one-second poll would make every quick
 * job feel slow; a large import runs for minutes, so a fixed 200ms poll would be
 * hundreds of pointless requests. Giving up entirely is the third case: a worker
 * that died leaves a job that will never move, and polling it forever is a
 * request every few seconds for as long as the tab is open.
 */
export function jobPollDelay(jobs: readonly Job[], now: number): number | false {
  const running = jobs.filter(isJobRunning)
  if (running.length === 0) return false

  const oldest = Math.min(...running.map((job) => Date.parse(job.created_on)))
  // An unreadable date leaves this `NaN`, and every comparison below is then
  // false: no back-off step applies and the give-up threshold is not reached, so
  // the job is polled at the starting rate as though it had just begun. That is
  // the reading worth having -- a few extra requests rather than a progress bar
  // frozen at whatever it last showed -- and it needs no special case to get it.
  const elapsed = now - oldest

  if (elapsed >= GIVE_UP_AFTER) return false

  let delay = FIRST_DELAY
  for (const step of STEPS) {
    if (elapsed >= step.after) delay = step.delay
  }
  return delay
}
